import { useCallback, useEffect, useRef } from 'react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import z from 'zod';

import { useSSOCallback } from '@/api/endpoints/auth';
import { parseSSOPopupName, type PopupMessageData, SSO_BROADCAST_CHANNEL_NAME } from '@/hooks/use-popup-window';
import { Logo } from '@/modus/Logo';
import { extractHTTPError } from '@/utils/http-client';
import { safeInvoke } from '@/utils/safe-invoke';

export const Route = createFileRoute('/_public/callback')({
  component: CallbackPage,
  validateSearch: z.object({
    code: z.string().optional(),
    state: z.string().optional(),
    error: z.string().optional(),
    error_description: z.string().optional(),
  }),
});

const POPUP_CLOSE_DELAY_MS = 10;

/**
 * Notify the opener of the SSO result over both available channels. The BroadcastChannel
 * is the reliable path — it survives the Cross-Origin-Opener-Policy severance of
 * `window.opener` that happens after the popup round-trips through a cross-origin IdP —
 * while `window.opener.postMessage` remains a best-effort fallback for environments where
 * the opener reference is still intact.
 */
function notifyOpener(message: PopupMessageData, token: string | null) {
  if (typeof window === 'undefined') return;

  // Stamp the opener's correlation token so it can distinguish this login's result from
  // any other message on the shared channel.
  const payload: PopupMessageData = token ? { ...message, token } : message;

  if (typeof BroadcastChannel !== 'undefined') {
    const channel = new BroadcastChannel(SSO_BROADCAST_CHANNEL_NAME);
    safeInvoke(() => channel.postMessage(payload));
    channel.close();
  }

  if (window.opener) {
    safeInvoke(() => window.opener.postMessage(payload, window.location.origin));
  }
}

function CallbackPage() {
  const router = useRouter();
  const { code, state, error, error_description } = Route.useSearch();

  // The opener encodes a per-login token into the popup's window.name. It persists across
  // the cross-origin IdP round-trip (unlike `window.opener`, which COOP commonly severs),
  // so a non-null parse is the reliable "we are the SSO popup" signal, and the token is
  // echoed back so the opener only accepts its own login's result. `window.opener` stays
  // as a fallback detector for the rare case where the IdP clobbers window.name.
  const popupToken = typeof window !== 'undefined' ? parseSSOPopupName(window.name) : null;
  const isPopup = popupToken !== null || (typeof window !== 'undefined' && window.opener !== null);
  const hasProcessedRef = useRef(false);

  const [processCallback] = useSSOCallback();

  const handleCallback = useCallback(async () => {
    const errorMessage = error
      ? error_description || error || 'Authentication failed'
      : !code
        ? 'No authorization code received'
        : !state
          ? 'No OAuth state received'
          : null;

    if (errorMessage) {
      if (isPopup) {
        notifyOpener({ type: 'sso-error', error: errorMessage }, popupToken);
        setTimeout(() => window.close(), POPUP_CLOSE_DELAY_MS);
        return;
      }
      router.navigate({ to: '/login' });
      return;
    }

    if (!code || !state) return;

    const redirectUri = window.location.origin + router.buildLocation({ to: '/callback' }).href;

    const { error: processError } = await processCallback({
      code,
      redirect_uri: redirectUri,
      state,
    });

    if (processError) {
      const processErrorMessage = extractHTTPError(processError, 'An unexpected error occurred');
      console.error('[SSO CALLBACK] Error processing callback:', processErrorMessage);

      if (isPopup) {
        notifyOpener({ type: 'sso-error', error: processErrorMessage }, popupToken);
        setTimeout(() => window.close(), POPUP_CLOSE_DELAY_MS);
        return;
      }

      router.navigate({ to: '/login' });
      return;
    }

    if (isPopup) {
      notifyOpener({ type: 'sso-success' }, popupToken);
      setTimeout(() => window.close(), POPUP_CLOSE_DELAY_MS);
      return;
    }
    router.invalidate();
    router.navigate({ to: '/' });
  }, [code, state, error, error_description, isPopup, popupToken, processCallback, router]);

  useEffect(() => {
    if (!code && !error) return;
    if (hasProcessedRef.current) return;

    hasProcessedRef.current = true;
    handleCallback();
  }, [code, error, handleCallback]);

  return (
    <main className="h-screen w-screen flex items-center justify-center">
      <Logo size={76} loading />
    </main>
  );
}
